import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../utils/firebaseConfig.js";
import { FaTrash, FaPlus } from "react-icons/fa";
import "../Styles/Admin.css";

const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8 MB
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const ManagePhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const refreshPhotos = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "carouselPhotos"),
        orderBy("createdAt", "desc"),
      );
      const snap = await getDocs(q);
      const docs = [];
      snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
      setPhotos(docs);
    } catch (err) {
      console.log("Error loading photos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshPhotos();
  }, []);

  const handleFileChange = (e) => {
    setErrorMsg("");
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (!ACCEPTED.includes(f.type)) {
      setErrorMsg("Επιτρέπονται μόνο εικόνες (JPG, PNG, WEBP, GIF).");
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setErrorMsg("Η εικόνα ξεπερνά τα 8 MB.");
      setFile(null);
      return;
    }
    setFile(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setErrorMsg("Επιλέξτε μια εικόνα.");
      return;
    }
    setUploading(true);
    setErrorMsg("");

    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storagePath = `carousel/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await addDoc(collection(db, "carouselPhotos"), {
        url,
        storagePath,
        caption: caption.trim(),
        createdAt: serverTimestamp(),
      });

      setFile(null);
      setCaption("");
      const fileInput = document.getElementById("manage-photos-file-input");
      if (fileInput) fileInput.value = "";
      await refreshPhotos();
    } catch (err) {
      console.log("Error uploading photo:", err);
      setErrorMsg("Σφάλμα κατά την αποστολή. Δοκιμάστε ξανά.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photo) => {
    const confirmed = window.confirm("Διαγραφή φωτογραφίας;");
    if (!confirmed) return;
    try {
      if (photo.storagePath) {
        try {
          await deleteObject(ref(storage, photo.storagePath));
        } catch (err) {
          // Storage object may already be missing — keep going so the
          // Firestore record gets removed regardless.
          console.log("Storage delete warning:", err);
        }
      }
      await deleteDoc(doc(db, "carouselPhotos", photo.id));
      await refreshPhotos();
    } catch (err) {
      console.log("Error deleting photo:", err);
    }
  };

  return (
    <div className="manage_photos">
      <form className="manage_photos_form" onSubmit={handleUpload}>
        <label htmlFor="manage-photos-file-input" className="file-picker">
          <FaPlus />
          <span>{file ? file.name : "Προσθηκη φωτο"}</span>
          <input
            id="manage-photos-file-input"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </label>

        <input
          type="text"
          placeholder="Λεζαντα (προαιρετικο)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          disabled={uploading}
          maxLength={140}
        />

        <button type="submit" disabled={uploading || !file}>
          {uploading ? "Ανεβασμα..." : "Αποστολη"}
        </button>

        {errorMsg && <p className="manage_photos_error">{errorMsg}</p>}
      </form>

      <hr />

      {loading ? (
        <p>Loading...</p>
      ) : photos.length === 0 ? (
        <p className="manage_photos_empty">Δεν υπάρχουν φωτογραφίες ακόμα.</p>
      ) : (
        <ul className="manage_photos_list">
          {photos.map((p) => (
            <li key={p.id}>
              <img src={p.url} alt={p.caption || ""} />
              <div className="manage_photos_meta">
                <span className="manage_photos_caption">
                  {p.caption || <em>χωρίς λεζάντα</em>}
                </span>
                <button
                  type="button"
                  className="manage_photos_delete"
                  onClick={() => handleDelete(p)}
                  aria-label="Διαγραφή"
                >
                  <FaTrash />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ManagePhotos;
